import { calculerMrr } from '../api/mrr'
import { fcfa, nombre } from '../api/format'
import type { DonneesPlateforme } from '../api/types'
import { Icone, type NomIcone } from './Icone'

interface Indicateur {
  libelle: string
  valeur: string
  detail: string
  icone: NomIcone
}

/**
 * La bande d'indicateurs. Le MRR et la valeur potentielle des essais viennent
 * des prix de la table `plans` et des prix figés des abonnements (jamais d'un
 * barème codé en dur), options comprises (véhicules supplémentaires, boîtiers
 * GPS) ; un abonnement annuel est ramené au mois (total annuel / 12) ; un essai
 * échu n'est compté nulle part.
 */
export function Kpis({ donnees }: { donnees: DonneesPlateforme }) {
  const { organisations, plans, chargeLe } = donnees
  const tenants = organisations.filter((o) => !o.is_operator)
  const actifs = tenants.filter((o) => o.sub_status === 'active').length
  const revenu = organisations.reduce((s, o) => s + o.revenue_fcfa, 0)
  const livreurs = organisations.reduce((s, o) => s + o.drivers, 0)
  const vehicules = organisations.reduce((s, o) => s + o.vehicles, 0)
  const courses = organisations.reduce((s, o) => s + o.courses_30d, 0)

  let mrrV = '—'
  let mrrD = 'tarifs des plans illisibles'
  let essaisV = '—'
  let essaisD = 'tarifs des plans illisibles'
  if (plans) {
    const m = calculerMrr(organisations, plans, chargeLe)
    mrrV = fcfa(m.mrr)
    const morceaux = [`${m.nbPayants} abonnement(s) payant(s)`]
    if (m.mrrOptions) morceaux.push(`dont ${fcfa(m.mrrOptions)} d'options`)
    if (m.nbAnnuels) morceaux.push(`${m.nbAnnuels} annuel(s) ramené(s) au mois`)
    if (m.nbImpayes) morceaux.push(`dont ${fcfa(m.mrrImpaye)} en impayé`)
    if (m.sansPrix) morceaux.push(`${m.sansPrix} sur devis non chiffré(s)`)
    mrrD = morceaux.join(' · ')
    essaisV = fcfa(m.potentielEssais)
    essaisD = `${m.nbEssais} essai(s) en cours`
    if (m.nbEssaisEchus) essaisD += ` · ${m.nbEssaisEchus} échu(s) non compté(s)`
  }

  const K: Indicateur[] = [
    { libelle: 'Tenants', valeur: nombre(tenants.length), detail: `${actifs} abonnement(s) actif(s)`, icone: 'building' },
    { libelle: 'MRR', valeur: mrrV, detail: mrrD, icone: 'chart' },
    { libelle: 'Valeur des essais', valeur: essaisV, detail: essaisD + ' · potentiel, hors MRR', icone: 'hourglass' },
    { libelle: 'Revenu encaissé', valeur: fcfa(revenu), detail: 'paiements confirmés, cumul', icone: 'card' },
    { libelle: 'Flotte supervisée', valeur: nombre(vehicules), detail: `${nombre(livreurs)} livreur(s)`, icone: 'truck' },
    { libelle: 'Courses (30 j)', valeur: nombre(courses), detail: 'toutes organisations', icone: 'package' },
  ]

  return (
    <section className="kpis" aria-label="Indicateurs clés">
      {K.map((k) => (
        <div className="carte kpi" key={k.libelle}>
          <div className="kpi-tete">
            <span className="kpi-lib">{k.libelle}</span>
            <span className="puce"><Icone nom={k.icone} /></span>
          </div>
          <div className="kpi-v">{k.valeur}</div>
          <div className="kpi-d">{k.detail}</div>
        </div>
      ))}
    </section>
  )
}
